import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import CommonUtils from 'utilities/common-utils';
import { FilterType } from 'utilities/enum-utils';
import { IFilter } from 'interfaces/filter-interfaces';
import { Text } from '@good/ui/core';
import { CustomerAdditionalInfos } from 'common-components/tooltips';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { IRootState } from 'stores/rematch/root-store';

export function useSearchedItemList(filter: IFilter) {
  const [searchedItemList, setSearchedItemList] = useState<unknown[]>([]);
  const {
    customersLite,
    workerList,
    batchAuthors,
    sessions,
    groupServiceSchedules,
    suburbs,
    caseManagers,
    supportCoordinators,
    debtors,
  } = useSelector((state: IRootState) => ({
    customersLite: state.customersStore.customersLite,
    workerList: state.teamStore.workerList,
    batchAuthors: state.billingsStore.batchAuthors,
    sessions: state.groupServiceStore.sessions,
    groupServiceSchedules: state.servicesStore.groupServiceSchedules,
    suburbs: state.bookingsStore.suburbs,
    caseManagers: state.customersStore.caseManagers,
    supportCoordinators: state.customersStore.supportCoordinators,
    debtors: state.accountStore.debtors,
  }));

  useEffect(() => {
    if (filter.filter !== FilterType.CUSTOMER) {
      return;
    }

    setSearchedItemList(
      customersLite?.map((customer) => {
        return {
          name: `${customer.firstName} ${customer.lastName}`,
          displayText: (
            <>
              {`${customer.firstName} ${customer.lastName}`}
              <Text c='dimmed' span inherit>
                {customer.locality && ` (${customer.locality})`}
              </Text>
            </>
          ),
          value: customer.userId,
          tooltip: <CustomerAdditionalInfos customer={customer} getOnlyContent={true} />,
        };
      }) ?? [],
    );
  }, [customersLite, filter.filter]);

  useEffect(() => {
    if (
      ![FilterType.WORKER, FilterType.PREFERRED_SUPPORT_WORKER, FilterType.BLOCKED_SUPPORT_WORKER].includes(
        filter.filter,
      )
    ) {
      return;
    }

    setSearchedItemList(
      workerList.map((worker) => {
        return { displayText: `${worker.firstName} ${worker.lastName}`, value: worker.supportWorkerId };
      }),
    );
  }, [workerList, filter.filter]);

  useEffect(() => {
    if (filter.filter !== FilterType.BATCH_AUTHOR) {
      return;
    }

    setSearchedItemList(
      _.map(batchAuthors, (portalUser) => {
        return { displayText: `${portalUser.firstName} ${portalUser.lastName}`, value: portalUser.userId };
      }),
    );
  }, [batchAuthors, filter.filter]);

  useEffect(() => {
    if (filter.filter !== FilterType.LOCATION_BY_SUBURBS) {
      return;
    }

    setSearchedItemList(
      suburbs.map((suburb) => {
        return {
          displayText: (
            <>
              {suburb.suburb} ({suburb.postcode}) -{' '}
              <Text c='dimmed' span inherit>
                {suburb.state}
              </Text>
            </>
          ),
          value: suburb.postcode,
        };
      }),
    );
  }, [suburbs, filter.filter]);

  useEffect(() => {
    if (filter.filter !== FilterType.DEBTOR) {
      return;
    }

    setSearchedItemList(
      debtors.map((debtor) => {
        return {
          name: debtor.debtorName,
          displayText: <>{debtor.debtorName}</>,
          value: debtor.debtorId,
        };
      }),
    );
  }, [debtors, filter.filter]);

  useEffect(() => {
    if (filter.filter !== FilterType.SERVICE_DATE_TIMES) {
      return;
    }

    setSearchedItemList(
      sessions?.map((session) => {
        return {
          displayText: (
            <>
              <Text span inherit>
                {moment.tz(session.startDateTime, session.timezone).format('DD/MM/YY')}
              </Text>{' '}
              <Text c='dimmed' span inherit>
                {`${moment.tz(session.startDateTime, session.timezone).format('h:mm a')} - ${moment.tz(session.endDateTime, session.timezone).format('h:mm a')}`}
              </Text>
              <br />
              <Text span inherit>
                {session.description ? session.description : session.scheduleName ? session.scheduleName : 'One-off'} -{' '}
                {session.serviceName}
              </Text>
            </>
          ),
          value: session.serviceDateTimeId,
          tooltip: (
            <div className={'p-medium'} style={{ minWidth: '300px' }}>
              <div className={'mb-medium'}>
                <Text size='md' fw={600} c='white'>
                  Session times
                </Text>
                <br />
                {moment
                  .tz(session.startDateTime, session.timezone)
                  .startOf('day')
                  .isSame(moment.tz(session.endDateTime, session.timezone).startOf('day')) ? (
                  <Text c='white'>
                    {moment.tz(session.startDateTime, session.timezone).format('ddd, DD MMM YYYY')}
                    <br />
                    {moment.tz(session.startDateTime, session.timezone).format('h:mm a - ')}
                    {moment.tz(session.endDateTime, session.timezone).format('h:mm a')}
                  </Text>
                ) : (
                  <Text c='white'>
                    {moment.tz(session.startDateTime, session.timezone).format('ddd, DD MMM YYYY h:mm a')}
                    <br />
                    {moment.tz(session.endDateTime, session.timezone).format('ddd, DD MMM YYYY h:mm a')}
                  </Text>
                )}
              </div>
              <div className={'mb-medium'}>
                <Text size='md' fw={600} c='white'>
                  Schedule
                </Text>
                <br />
                <Text c='white'>{session.scheduleName ? session.scheduleName : 'One-off'}</Text>
              </div>
              <div className={'mb-medium'}>
                <Text size='md' fw={600} c='white'>
                  Session description
                </Text>
                <br />
                <Text c='white'>{session.description ? session.description : '-'}</Text>
              </div>
              <div className={'mb-medium'}>
                <Text size='md' fw={600} c='white'>
                  Service
                </Text>
                <br />
                <Text c='white'>{session.serviceName}</Text>
              </div>
              <div className={'mb-medium'}>
                <Text size='md' fw={600} c='white'>
                  Location
                </Text>
                <br />
                <Text c='white' style={{ whiteSpace: 'pre-line' }}>
                  {session.address ? CommonUtils.formatFullAddress(session.address) : '-'}
                </Text>
              </div>
            </div>
          ),
        };
      }) ?? [],
    );
  }, [sessions, filter.filter]);

  useEffect(() => {
    if (filter.filter !== FilterType.GROUP_SERVICE_SCHEDULES) {
      return;
    }

    setSearchedItemList(
      _.map(groupServiceSchedules, (schedule) => {
        return {
          displayText: (
            <>
              <Text>
                {moment.tz(schedule.scheduleStartDate, schedule.timezone).format('DD/MM/YY')} -{' '}
                {moment.tz(schedule.scheduleEndDate, schedule.timezone).format('DD/MM/YY')}
              </Text>
              <br />
              <Text>
                {schedule.scheduleName ? schedule.scheduleName : 'One-off'} - {schedule.serviceName}
              </Text>
            </>
          ),
          value: schedule.serviceScheduleId,
          tooltip: (
            <div className={'p-medium'} style={{ minWidth: '300px' }}>
              <div className={'mb-medium'}>
                <Text size={'regular'} weight={'bold'} color={'white'}>
                  Schedule dates
                </Text>
                <br />
                <Text color={'white'}>
                  {moment.tz(schedule.scheduleStartDate, schedule.timezone).format('ddd, DD MMM YYYY')}
                  <br />
                  {moment.tz(schedule.scheduleEndDate, schedule.timezone).format('ddd, DD MMM YYYY')}
                </Text>
              </div>
              <div className={'mb-medium'}>
                <Text size={'regular'} weight={'bold'} color={'white'}>
                  Name
                </Text>
                <br />
                <Text color={'white'}>{schedule.scheduleName ? schedule.scheduleName : 'One-off'}</Text>
              </div>
              <div className={'mb-medium'}>
                <Text size={'regular'} weight={'bold'} color={'white'}>
                  Service
                </Text>
                <br />
                <Text color={'white'}>{schedule.serviceName}</Text>
              </div>
              <div className={'mb-medium'}>
                <Text size={'regular'} weight={'bold'} color={'white'}>
                  Location
                </Text>
                <br />
                <Text color={'white'} style={{ whiteSpace: 'pre-line' }}>
                  {schedule.address ? CommonUtils.formatFullAddress(schedule.address) : '-'}
                </Text>
              </div>
            </div>
          ),
        };
      }),
    );
  }, [groupServiceSchedules, filter.filter]);

  useEffect(() => {
    if (filter.filter !== FilterType.CASE_MANAGER) {
      return;
    }

    setSearchedItemList(
      _.map(caseManagers, (caseManager) => ({
        displayText: caseManager.fullName,
        value: caseManager.userContactId,
      })),
    );
  }, [caseManagers, filter.filter]);

  useEffect(() => {
    if (filter.filter !== FilterType.SUPPORT_COORDINATOR) {
      return;
    }

    setSearchedItemList(
      _.map(supportCoordinators, (supportCoordinator) => ({
        displayText: supportCoordinator.fullName,
        value: supportCoordinator.supportCoordinatorId,
      })),
    );
  }, [supportCoordinators, filter.filter]);

  return [searchedItemList, setSearchedItemList] as const;
}
